import { getRequest, postRequest } from "../http/httpRequest";
import { throwIfNotOk } from "../helpers/ApiErrorHelper";
import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { RepresentativeImageResult } from "../../interfaces/RepresentativeImageObject";

export interface AddRepresentativeImagePayload {
  imageName: string;
  imageUrl: string;
  description?: string;
}

// Defensive client-side cap in case the backend ever returns an unbounded
// result set for a broad query — keeps the results grid from rendering an
// unreasonable number of thumbnails. Confirm with backend whether the search
// endpoint already paginates/limits before relying on this long-term.
const MAX_RESULTS = 60;

/**
 * Searches the shared representative-image catalog by (partial) name — powers
 * the "I don't have my own photos" search picker in EssentialDetails.
 *
 * Backend contract: GET /api/v1/representative-images/search?name=<query>,
 * returning the usual { errorInformation, generalProcessingStatus, contents }
 * envelope with `contents` being the flat result array directly (no nested
 * `products`/wrapper key, unlike most other products/* endpoints).
 *
 * Returns [] for a blank query rather than hitting the network — the caller
 * (RepresentativeImageSearch) already gates on a minimum query length before
 * debouncing, this is just a defensive floor.
 */
async function search(name: string): Promise<RepresentativeImageResult[]> {
  const trimmed = name.trim();
  if (!trimmed) return [];

  const endpoint = `${ServiceEndpoints.REPRESENTATIVE_IMAGE_SEARCH}?name=${encodeURIComponent(trimmed)}`;
  const includeBearerToken = true;
  const response = await getRequest(endpoint, includeBearerToken);
  await throwIfNotOk(response);
  const data = await response.json();
  const contents = Array.isArray(data?.contents) ? data.contents : [];
  return contents.slice(0, MAX_RESULTS);
}

/**
 * Registers a new entry in the shared representative-image catalog.
 *
 * Backend contract: POST /api/v1/manage/representative-images/add, body
 * { imageName, imageUrl, description? }, returning the usual
 * { errorInformation, generalProcessingStatus, contents } envelope with the
 * created record (including its server-assigned imageId/createdAt) as
 * contents[0].
 */
async function add(
  payload: AddRepresentativeImagePayload
): Promise<RepresentativeImageResult> {
  const response = await postRequest(ServiceEndpoints.REPRESENTATIVE_IMAGE_ADD, payload, true);
  await throwIfNotOk(response);
  const data = await response.json();
  const created = data?.contents?.[0];
  if (!created) {
    throw new Error("Couldn't add this image. Please try again.");
  }
  return created;
}

export default { search, add };
